import React, { FC } from 'react';
import { preventNonNumericValue } from 'utils/helpers';
import { ValidationErrors } from 'constants/validations';
import { EqualIcon } from 'components/common/icons/equalIcon';
import { Price } from 'feature/clientTabs/shipping/specialRates';
import { Button, Col, Form, InputNumber, Row, Select, Typography } from 'components';
import { CloseIcon, CloseIconLight, DoneIcon, EditIconLight } from 'components/common/icons';
import styles from 'components/common/ContactFieldManager/ContactInfoCard/styles.module.scss';
import {
  SHIPPING_MEASUREMENT_RANGE_KG,
  SHIPPING_MEASUREMENT_RANGE_MAPPING,
  SHIPPING_MEASUREMENT_RANGE_VOLUME,
  SHIPPING_RATE_METRICS,
  SHIPPING_RATE_METRICS_MAPPING,
} from 'staticData/form/client/shipping';

const { Item } = Form;
const { Text } = Typography;

interface IPrice {
  handlePriceChange?: <K extends keyof Price>(value: string, index: number, key: K) => void;
  onPriceRowRemove: (id: string) => void;
  onPriceRowEdit: (id: string) => void;
  onPriceRowSave: (id: string) => void;
  onPriceRowCancel: () => void;
  data: Price;
  priceList: Price[];
  isEditeMode: boolean;
}

export const PriceRow: FC<IPrice> = React.memo((props) => {
  const { data, priceList, onPriceRowRemove, onPriceRowEdit, isEditeMode, onPriceRowSave, onPriceRowCancel } = props;
  const { metrics, measurementRange, price } = data;
  return (
    <Col span={24}>
      {!isEditeMode && (
        <Row className={styles.main} align={'middle'} justify={'space-between'}>
          <Col>
            <Row gutter={16} align={'middle'}>
              <Col>
                <Text className={'font-weight-500'}>
                  {SHIPPING_RATE_METRICS_MAPPING[metrics]}: {SHIPPING_MEASUREMENT_RANGE_MAPPING[measurementRange]}
                </Text>
              </Col>
              <Col>
                <Text className={'font-weight-500'}>{price} €</Text>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row gutter={12} align={'middle'}>
              <Col>
                <Button
                  icon={<EditIconLight />}
                  type={'text'}
                  size={'small'}
                  onClick={() => onPriceRowEdit(measurementRange)}
                />
              </Col>
              <Col>
                <Button
                  size={'small'}
                  type={'text'}
                  icon={<CloseIconLight />}
                  onClick={() => onPriceRowRemove(measurementRange)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      {isEditeMode && (
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={[8, 0]} align={'top'} justify={'space-between'} className={'margin-t-6'}>
              <Col span={8}>
                <Item
                  name="edit_metrics"
                  label={'Metrics'}
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Metrics'),
                    },
                  ]}
                >
                  <Select
                    options={SHIPPING_RATE_METRICS.map((i: { value: string; label: string }) => ({
                      ...i,
                      disabled: metrics !== i.value,
                    }))}
                    size={'large'}
                  />
                </Item>
              </Col>

              <Col span={8}>
                <Item
                  name="edit_measurementRange"
                  label={'Measurement Range'}
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Measurement Range'),
                    },
                  ]}
                >
                  <Select
                    options={(metrics === 'KG' ? SHIPPING_MEASUREMENT_RANGE_KG : SHIPPING_MEASUREMENT_RANGE_VOLUME).map(
                      (i: { value: string; label: string }) => ({
                        ...i,
                        disabled:
                          measurementRange !== i.value && priceList.some((price) => price.measurementRange === i.value),
                      })
                    )}
                    size={'large'}
                  />
                </Item>
              </Col>

              <Col style={{ paddingTop: 32 }}>
                <EqualIcon />
              </Col>

              <Col flex={'2'}>
                <Item
                  name="edit_price"
                  label={'Price'}
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Price'),
                    },
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === '') {
                          return Promise.resolve();
                        }
                        const regex = /^\d+(\.\d{1,2})?$/;
                        if (!regex.test(value) || parseFloat(value) <= 0) {
                          return Promise.reject(
                            new Error('Price must be a positive number with up to 2 decimal points.')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<number>
                    controls={false}
                    size={'large'}
                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                    style={{ width: '100%' }}
                    suffix={<Text type={'secondary'}>€</Text>}
                  />
                </Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={4} justify={'end'}>
              <Col>
                <Button color="default" variant="filled" onClick={onPriceRowCancel} icon={<CloseIcon />} />
              </Col>
              <Col>
                <Button onClick={() => onPriceRowSave(measurementRange)} type={'primary'} icon={<DoneIcon />} />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </Col>
  );
});
